import {
  BarChart3,
  DollarSign,
  Megaphone,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react-native';
import { type ComponentType, useMemo, useState } from 'react';
import { ActivityIndicator, Platform, useWindowDimensions, View } from 'react-native';
import { AnimatedCard } from '../../../components/animations';
import { ShimmerSkeleton } from '../../../components/animations/Skeleton';
import { Sparkline } from '../../../components/charts';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Card } from '../../../components/primitives/Card';
import { useCountUp } from '../../../hooks/useCountUp';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionTypography } from '../../../theme/typography';

type MetricType = 'revenue' | 'visitors' | 'conversion' | 'campaigns' | 'default';

export interface MetricData {
  id: string;
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: {
    value: number;
    direction: 'up' | 'down';
  };
  sparklineData?: number[];
  metricType?: MetricType;
}

interface StatsOverviewProps {
  metrics: MetricData[];
  loading?: boolean;
}

const metricColors: Record<
  MetricType,
  {
    accent: string;
    sparkline: string;
    icon: ComponentType<{ size: number; color: string; strokeWidth?: number }>;
  }
> = {
  revenue: {
    accent: colors.metrics.revenue,
    sparkline: colors.metrics.revenue,
    icon: DollarSign,
  },
  visitors: {
    accent: colors.metrics.visitors,
    sparkline: colors.metrics.visitors,
    icon: Users,
  },
  conversion: {
    accent: colors.metrics.conversion,
    sparkline: colors.metrics.conversion,
    icon: TrendingUp,
  },
  campaigns: {
    accent: colors.metrics.campaigns,
    sparkline: colors.metrics.campaigns,
    icon: Megaphone,
  },
  default: {
    accent: colors.primary.neon,
    sparkline: colors.primary.neon,
    icon: BarChart3,
  },
};

function getMetricTypeFromId(id: string): MetricType {
  if (id.includes('revenue')) return 'revenue';
  if (id.includes('visitor') || id.includes('lead')) return 'visitors';
  if (id.includes('conversion')) return 'conversion';
  if (id.includes('campaign')) return 'campaigns';
  return 'default';
}

function isEmptyValue(value: string | number): boolean {
  if (typeof value === 'number') return false;
  const trimmed = value.trim();
  return trimmed === '' || trimmed === '—' || trimmed === '-';
}

function parseNumericValue(value: string | number): {
  numeric: number;
  prefix: string;
  suffix: string;
  decimals: number;
} {
  if (typeof value === 'number') {
    return { numeric: value, prefix: '', suffix: '', decimals: 0 };
  }
  const match = /^(\D*?)([\d,.]+)(.*)$/.exec(value);
  if (!match) {
    return { numeric: 0, prefix: '', suffix: value, decimals: 0 };
  }
  const [, prefix, numStr, suffix] = match;
  const cleanNum = numStr.replaceAll(',', '');
  const dotIndex = cleanNum.indexOf('.');
  const decimals = dotIndex >= 0 ? cleanNum.length - dotIndex - 1 : 0;
  return { numeric: Number.parseFloat(cleanNum), prefix, suffix, decimals };
}

function formatAnimatedValue(
  value: number,
  decimals: number,
  prefix: string,
  suffix: string,
): string {
  const formatted = value.toFixed(decimals).replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${prefix}${formatted}${suffix}`;
}

function AnimatedMetricValue({ value }: Readonly<{ value: string | number }>) {
  const parsed = useMemo(() => parseNumericValue(value), [value]);
  const animatedNumber = useCountUp(parsed.numeric, 800);
  const displayValue = formatAnimatedValue(
    animatedNumber,
    parsed.decimals,
    parsed.prefix,
    parsed.suffix,
  );

  return (
    <Text style={[darkPrecisionTypography.metric, { fontVariant: ['tabular-nums'] }]}>
      {displayValue}
    </Text>
  );
}

function EmptyValuePlaceholder() {
  return (
    <Box style={{ paddingVertical: 4 }}>
      <ShimmerSkeleton width={96} height={32} borderRadius={6} />
    </Box>
  );
}

function StatCard({
  metric,
  index,
}: Readonly<{
  metric: MetricData;
  index: number;
}>) {
  const [isHovered, setIsHovered] = useState(false);
  const metricType = metric.metricType || getMetricTypeFromId(metric.id);
  const colorConfig = metricColors[metricType];
  const IconComponent = colorConfig.icon;
  const isEmpty = isEmptyValue(metric.value);
  const trendUp = metric.trend?.direction === 'up';
  const trendColor = trendUp ? colors.status.success.main : colors.status.error.main;

  const webHoverStyle =
    Platform.OS === 'web' && isHovered
      ? {
          borderColor: colorConfig.accent,
          transform: [{ translateY: -2 }] as never,
        }
      : null;

  const hoverProps =
    Platform.OS === 'web'
      ? {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        }
      : {};

  return (
    <AnimatedCard delay={index * 80} enablePress>
      <View {...hoverProps}>
        <Card
          variant="default"
          radius="xl"
          padding="lg"
          style={[{ minHeight: 168, position: 'relative', overflow: 'hidden' }, webHoverStyle]}
        >
          {metric.trend && (
            <View
              style={{
                position: 'absolute',
                top: tokens.spacing.lg,
                right: tokens.spacing.lg,
                paddingHorizontal: tokens.spacing.sm,
                paddingVertical: 2,
                borderRadius: tokens.radius.full,
                backgroundColor: colors.background.secondary,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {trendUp ? (
                <TrendingUp size={10} color={trendColor} strokeWidth={2.5} />
              ) : (
                <TrendingDown size={10} color={trendColor} strokeWidth={2.5} />
              )}
              <Text color={trendColor} style={{ fontWeight: '600', fontSize: 10, lineHeight: 12 }}>
                {Math.abs(metric.trend.value)}%
              </Text>
            </View>
          )}

          <VStack spacing="md" style={{ flex: 1, justifyContent: 'space-between' }}>
            <HStack spacing="sm" align="center" style={{ paddingRight: metric.trend ? 56 : 0 }}>
              <IconComponent size={14} color={colorConfig.accent} strokeWidth={2} />
              <Text
                color={colors.text.tertiary}
                style={[
                  darkPrecisionTypography.label,
                  { flexShrink: 1, color: colors.text.tertiary },
                ]}
                numberOfLines={2}
              >
                {metric.title}
              </Text>
            </HStack>

            <VStack spacing="xxs">
              {isEmpty ? <EmptyValuePlaceholder /> : <AnimatedMetricValue value={metric.value} />}
              {metric.subtitle && (
                <Text
                  color={colors.text.secondary}
                  style={[darkPrecisionTypography.metricSm, { color: colors.text.secondary }]}
                  numberOfLines={2}
                >
                  {metric.subtitle}
                </Text>
              )}
            </VStack>

            {metric.sparklineData && metric.sparklineData.length > 1 && (
              <Sparkline
                data={metric.sparklineData}
                width={120}
                height={20}
                color={colorConfig.sparkline}
                showGradient
                gradientOpacity={0.2}
              />
            )}
          </VStack>
        </Card>
      </View>
    </AnimatedCard>
  );
}

function LoadingCard({ index }: Readonly<{ index: number }>) {
  return (
    <AnimatedCard delay={index * 80}>
      <Card
        variant="default"
        radius="xl"
        padding="lg"
        style={{
          minHeight: 168,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ActivityIndicator size="small" color={colors.primary.neon} />
      </Card>
    </AnimatedCard>
  );
}

export function StatsOverview({ metrics, loading }: Readonly<StatsOverviewProps>) {
  const { width } = useWindowDimensions();
  const isDesktop = width >= tokens.breakpoints.desktop;
  const isTablet = width >= tokens.breakpoints.tablet;

  const gap = tokens.spacing.md;

  const resolveCardWidth = () => {
    if (isDesktop) return '25%';
    if (isTablet) return '33.333%';
    return '50%';
  };
  const cardWidth = resolveCardWidth();

  if (loading) {
    return (
      <Box flexDirection="row" flexWrap="wrap" style={{ margin: -gap / 2 }}>
        {[0, 1, 2, 3].map((i) => (
          <Box
            key={i}
            style={{
              width: cardWidth,
              padding: gap / 2,
            }}
          >
            <LoadingCard index={i} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box
      testID="dashboard_stats_container"
      flexDirection="row"
      flexWrap="wrap"
      style={{ margin: -gap / 2 }}
    >
      {metrics.map((metric, index) => (
        <Box
          key={metric.id}
          testID={`dashboard_stat_${metric.id}_card`}
          style={{
            width: cardWidth,
            padding: gap / 2,
          }}
        >
          <StatCard metric={metric} index={index} />
        </Box>
      ))}
    </Box>
  );
}
